import { useChainModal, useConnectModal } from '@rainbow-me/rainbowkit';
import { Button, HStack, Icon, Text, Tooltip } from '@chakra-ui/react';
import BigNumber from 'bignumber.js';
import { useAccount } from 'wagmi';
import { MdWallet } from 'react-icons/md';
import { requiredChainIds } from '../../utils/contracts';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import GetXEDModal from '../../components/GetXEDModal';

export default function StakingActions({
  stake,
  unstake,
  isStaking,
  needsApproval,
  approve,
  balance,
  isLoading,
}: {
  stake: () => void;
  unstake: () => void;
  isStaking: boolean;
  needsApproval: boolean;
  approve: () => void;
  balance: string;
  isLoading: boolean;
}) {
  const { openConnectModal } = useConnectModal();

  const { address, chainId } = useAccount();

  const isWrongNetwork = chainId && !requiredChainIds.includes(chainId);

  const { openChainModal } = useChainModal();

  const balanceNumber = new BigNumber(balance).div(10 ** 18);
  const hasBalance = new BigNumber(balanceNumber).isGreaterThan(0);

  if (!address) {
    return (
      <Button
        w="100%"
        borderRadius="full"
        bg="brand.500"
        _hover={{ bg: 'brand.400' }}
        color="white"
        onClick={openConnectModal}
      >
        <HStack spacing="1.5">
          <Icon as={MdWallet} fontSize="xl" />
          <Text>Connect Wallet</Text>
        </HStack>
      </Button>
    );
  }

  if (isWrongNetwork) {
    return (
      <Button
        w="100%"
        borderRadius="full"
        bg="red.500"
        _hover={{ bg: 'red.600' }}
        onClick={openChainModal}
      >
        Switch network to Ethereum
      </Button>
    );
  }

  if (isStaking) {
    return (
      <ConfirmDialog
        onConfirm={unstake}
        confirmText="Unstake"
        title="Unstake"
        cancelText="Cancel"
        description="Are you sure you want to unstake? By unstaking early you will lose your potential rewards."
      >
        <Button variant="secondary" w="100%" isLoading={isLoading}>
          Unstake
        </Button>
      </ConfirmDialog>
    );
  }

  if (!hasBalance) {
    return (
      <GetXEDModal w="100%">
        <Button variant="primary" w="100%">
          Get $XED
        </Button>
      </GetXEDModal>
    );
  }

  if (needsApproval) {
    return (
      <HStack w="100%">
        <Button w="100%" onClick={approve} isLoading={isLoading}>
          Approve
        </Button>
        <Tooltip label="You must first approve the staking contract to be able to transfer your tokens.">
          <Button w="100%" isDisabled>
            Stake
          </Button>
        </Tooltip>
      </HStack>
    );
  }

  return (
    <Button w="100%" onClick={stake} isLoading={isLoading}>
      Stake
    </Button>
  );
}
